import type { StoreLocationData } from "@/components/Table/StoreLocationFields.tsx";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { handleJsonApiError } from "jsonapi-zod-query";

type MailMergeResult = {
    blob: Blob;
    fileName: string;
};

type Props = {
    caseIdUUID: string;
    templateName: string;
    selectedProviderUUID?: string;
    storeLocation?: StoreLocationData;
};

export const useFetchMailMergeForCase = ({
    caseIdUUID,
    templateName,
    selectedProviderUUID,
    storeLocation,
}: Props): UseQueryResult<MailMergeResult> => {
    const fetch = useAuthenticatedFetch();

    return useQuery<MailMergeResult>({
        queryKey: [
            "case-mail-merge",
            caseIdUUID,
            templateName,
            selectedProviderUUID,
            storeLocation,
        ],
        queryFn: async () => {
            const response = await fetch(apiUrl("/case/mail-merge"), {
                method: "POST",
                headers: {
                    "Content-Type": "application/vnd.api+json",
                },
                body: JSON.stringify({
                    caseIdUUID,
                    templateName,
                    selectedProviderUUID,
                    storeLocation,
                }),
            });

            await handleJsonApiError(response);
            const blob = await response.blob();

            const contentDisposition = response.headers.get("Content-Disposition");
            const fileName = contentDisposition?.match(/filename="(.+?)"/)?.[1] || "document.docx";

            return { blob, fileName };
        },
        enabled: !!templateName,
    });
};
